import { useEffect, useRef, type JSX } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Self-preview ("show your face", ADR-0030) — a small, always-on view of the player's OWN outgoing
 * webcam while they're sharing. It is the cornerstone of consent-through-constant-awareness: you can
 * never be live without seeing EXACTLY what nearby ducks see (mirrored, the universal selfie convention,
 * so it doesn't feel wrong), plus a persistent "you're sharing · nearby" chip and a one-click ✕ kill
 * switch. Three redundant "you're live" tells (this, the lit Camera button, the gold in-world bubble)
 * make it impossible to be live and not know it.
 */
export interface SelfCameraPreviewProps {
  /** The player's own outgoing stream while live (null when off → the preview renders nothing). */
  stream: MediaStream | null;
  /** Stop sharing — the instant, no-confirm kill switch (also on the top-right Camera button). */
  onStop: () => void;
}

export function SelfCameraPreview({ stream, onStop }: SelfCameraPreviewProps): JSX.Element | null {
  const reduceMotion = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream;
    if (stream) void el.play().catch(() => undefined);
    return () => {
      el.srcObject = null;
    };
  }, [stream]);

  if (!stream) return null;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="pointer-events-auto absolute bottom-4 right-4 z-10 flex flex-col items-center gap-1"
    >
      <div className="relative h-24 w-24 overflow-hidden rounded-full border-2 border-amber-300/70 shadow-2xl">
        {/* Mirrored (-scale-x) selfie view — what nearby ducks see, framed the way you expect to see yourself. */}
        <video ref={videoRef} muted autoPlay playsInline className="h-full w-full -scale-x-100 object-cover" />
        <button
          type="button"
          onClick={onStop}
          aria-label="Stop sharing your camera"
          title="Stop sharing your camera"
          className="absolute right-0.5 top-0.5 rounded-full bg-black/60 px-1.5 text-xs font-bold text-white/90 transition hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>
      <span className="rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-amber-100 backdrop-blur-md">
        <span aria-hidden="true">📷</span> You’re sharing · nearby
      </span>
    </motion.div>
  );
}
